// The workspace's websites at a glance: key figures of all of them, and a card per website with the
// screenshot of its home page, its estimate and how it has moved, its failures and whether it is being
// scanned right now.
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CheckCircledIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  GlobeIcon,
  PlusIcon,
  ReloadIcon,
} from '@radix-ui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  can,
  captureFile,
  meQuery,
  overviewQuery,
  type ScanOutcome,
  type Site,
  type SiteOverview,
  sitesQuery,
} from '../api';
import { SiteFavicon } from '../site-favicon';
import { buttonClass, Field, HelpPopover, Message, Page } from '../ui';
import { publicPage } from '../util';
import { AnimatedNumber, ToneIcon, worstTone } from '../viz';
import { PRIORITY_TONE } from './results-parts';
import { OutcomeIcon } from './scan-status';

export function VerificationBadge({ status }: { status: Site['verification']['status'] }) {
  const { t } = useTranslation();
  return (
    <span className="status-badge" data-status={status}>
      {status === 'verified' && <CheckCircledIcon aria-hidden="true" />}
      {t(`sites.status.${status}`)}
    </span>
  );
}

/**
 * What the workspace comes to, in three figures: how many websites there are (all of them), how many pages
 * the latest analysis of each website covered and which problems those analyses detect. A problem type is a
 * kind of failure (one per rule) and an affected element is each place where it shows up: they are told apart,
 * in the units of the results of each website. Only the latest completed analysis of each website counts
 * (what people discarded is out), so the hint says how many websites that is when some have none yet; a
 * correction counts only when a new analysis no longer detects it. The average of the estimates is not here: it mixes websites that have little
 * in common and each card already has its own.
 */
function Overview({ sites, overview }: { sites: Site[]; overview: SiteOverview[] }) {
  const { t } = useTranslation();
  const analysed = overview.filter((entry) => entry.lastDone !== null);
  const pages = analysed.reduce((sum, entry) => sum + (entry.lastDone?.stats?.pagesScanned ?? 0), 0);
  const problems = analysed.reduce((sum, entry) => sum + (entry.pending?.problems ?? 0), 0);
  const elements = analysed.reduce((sum, entry) => sum + (entry.pending?.elements ?? 0), 0);
  // As in the analysis: «79+» when the engine stopped counting elements of some check.
  const capped = analysed.some((entry) => entry.pending?.capped);
  // Of those problems, the ones that are shared components (the same element on several pages): global.
  const global = analysed.reduce((sum, entry) => sum + (entry.pending?.global ?? 0), 0);
  const verified = sites.filter((site) => site.verification.status === 'verified').length;
  return (
    <dl className="home-figures">
      <div>
        <dt>{t('home.figures.sites')}</dt>
        <dd>
          <strong>
            <AnimatedNumber value={sites.length} />
          </strong>
          <span className="figure-hint">{t('home.figures.verified', { count: verified })}</span>
        </dd>
      </div>
      <div>
        <dt>{t('home.figures.pages')}</dt>
        <dd>
          <strong>
            <AnimatedNumber value={pages} />
          </strong>
          <span className="figure-hint">
            {analysed.length < sites.length
              ? t('home.figures.pagesHintSome', { analysed: analysed.length, total: sites.length })
              : t('home.figures.pagesHint')}
          </span>
        </dd>
      </div>
      <div data-tone={analysed.length > 0 && problems > 0 ? 'critica' : undefined}>
        <dt>
          {t('home.figures.problems')}
          <HelpPopover label={t('home.figures.problemsHelp')}>
            <p>{t('home.figures.problemsHelpText')}</p>
            {capped && <p>{t('home.figures.problemsHelpCapped')}</p>}
          </HelpPopover>
        </dt>
        <dd>
          {analysed.length === 0 ? (
            <>
              <strong aria-hidden="true">–</strong>
              <span className="figure-hint">{t('home.figures.none')}</span>
            </>
          ) : (
            <>
              <strong>
                <AnimatedNumber value={problems} />
              </strong>
              <span className="figure-hint">
                {t(capped ? 'home.figures.elementsCapped' : 'home.figures.elements', { count: elements })}
                {global > 0 && ` · ${t('home.figures.global', { count: global })}`}
              </span>
            </>
          )}
        </dd>
      </div>
    </dl>
  );
}

type CardState = 'pending' | ScanOutcome;

/** What the card tells about the website: nothing analysed yet, or the state of its latest analysis. */
function cardState(overview?: SiteOverview): CardState {
  return overview?.latest ? overview.latest.outcome : 'pending';
}

/**
 * The findings in the units of the analysis' own summary: how many kinds of problem (one per rule) and, in words,
 * how urgent they are, with the elements affected apart and what a person still has to review. Never an icon with
 * only a number, and never the engine's raw counts: those add up per page and would not match the summary.
 */
function CardFindings({
  pending,
  partial,
}: {
  pending: NonNullable<SiteOverview['pending']>;
  partial: boolean;
}) {
  const { t } = useTranslation();
  const present = (['alta', 'media', 'baja'] as const).filter((priority) => pending.byPriority[priority] > 0);
  const nothing = pending.problems === 0 && pending.review === 0;
  // A partial analysis never says that nothing was found: pages are missing.
  if (nothing && partial) return null;
  return (
    <div className="site-card-findings">
      {pending.problems > 0 && (
        <p>
          <strong>{t('home.card.problems', { count: pending.problems })}</strong>
          <span className="site-card-muted">
            {' · '}
            {t(pending.capped ? 'home.figures.elementsCapped' : 'home.figures.elements', {
              count: pending.elements,
            })}
          </span>
        </p>
      )}
      <ul className="tone-chips">
        {present.map((priority) => (
          <li key={priority} className="tone-chip" data-tone={PRIORITY_TONE[priority]}>
            <ToneIcon tone={PRIORITY_TONE[priority]} />
            {t(`home.card.priority.${priority}`, { count: pending.byPriority[priority] })}
          </li>
        ))}
        {nothing && (
          <li className="tone-chip" data-tone="ok">
            <ToneIcon tone="ok" />
            {t('viz.chips.ok')}
          </li>
        )}
        {pending.review > 0 && (
          <li className="tone-chip" data-tone="review">
            <ToneIcon tone="review" />
            {t('viz.chips.review', { count: pending.review })}
          </li>
        )}
      </ul>
    </div>
  );
}

/** The ownership of the website, apart from the analysis: it says nothing about whether it was analysed. */
function CardOwnership({ status }: { status: Site['verification']['status'] }) {
  const { t } = useTranslation();
  return (
    <p className="site-card-ownership" data-status={status}>
      {status === 'verified' ? (
        <CheckCircledIcon aria-hidden="true" />
      ) : status === 'checking' ? (
        <ReloadIcon aria-hidden="true" />
      ) : (
        <ExclamationTriangleIcon aria-hidden="true" />
      )}
      {t(`home.card.ownership.${status}`)}
    </p>
  );
}

const PREVIEW_RETRIES = 4;
const PREVIEW_RETRY_MS = 4000;

/**
 * The picture of the home page for a card whose analysis kept none: the one the worker took when the project
 * was created. It may not be ready yet, so it is asked for again a few times; without it (the page could not
 * be captured) the generic drawing stays.
 */
function useHomePreview(siteId: string, enabled: boolean) {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!enabled) {
      setAttempt(0);
      setFailed(false);
      return;
    }
    if (!failed || attempt >= PREVIEW_RETRIES) return;
    const timer = setTimeout(() => {
      setAttempt(attempt + 1);
      setFailed(false);
    }, PREVIEW_RETRY_MS);
    return () => clearTimeout(timer);
  }, [enabled, failed, attempt]);
  return {
    src:
      enabled && !failed
        ? `/api/app/sites/${siteId}/preview${attempt > 0 ? `?intento=${attempt}` : ''}`
        : null,
    onError: () => setFailed(true),
  };
}

/**
 * The visual header of a card: the screenshot of the home page the analysis kept or, before any analysis, the
 * picture taken when the project was created; the same drawing for every website that has neither (or whose
 * image fails), so that all the cards start the same way. It is not a link: the whole card leads to the
 * project through the name (see `.site-card-link`).
 */
function CardHeader({
  site,
  thumbnail,
  cardPreview,
}: {
  site: Site;
  thumbnail: SiteOverview['thumbnail'];
  /** Settings asked for a new capture: show it instead of the analysis thumbnail. */
  cardPreview?: boolean;
}) {
  const [broken, setBroken] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const preferPreview = Boolean(cardPreview);
  const hasScanThumbnail = Boolean(thumbnail) && !broken && !preferPreview;
  const preview = useHomePreview(site.id, preferPreview || !hasScanThumbnail);
  const src = hasScanThumbnail ? captureFile(thumbnail!.scanId, thumbnail!.name) : preview.src;
  const showsImage = src !== null && (hasScanThumbnail || loaded);
  return (
    <div className="site-card-shot" aria-hidden="true" data-placeholder={showsImage ? undefined : true}>
      {src !== null && (
        <img
          src={src}
          alt=""
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => (hasScanThumbnail ? setBroken(true) : preview.onError())}
        />
      )}
      {!showsImage && (
        <svg viewBox="0 0 160 80" className="site-card-placeholder" aria-hidden="true">
          <rect x="30" y="12" width="100" height="62" rx="6" fill="currentColor" fillOpacity="0.1" />
          <rect
            x="30"
            y="12"
            width="100"
            height="62"
            rx="6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path d="M30 26h100" stroke="currentColor" strokeWidth="2" />
          <circle cx="38" cy="19" r="2" fill="currentColor" />
          <circle cx="45" cy="19" r="2" fill="currentColor" />
          <circle cx="52" cy="19" r="2" fill="currentColor" />
          <rect x="42" y="36" width="42" height="6" rx="3" fill="currentColor" fillOpacity="0.55" />
          <rect x="42" y="48" width="76" height="4" rx="2" fill="currentColor" fillOpacity="0.3" />
          <rect x="42" y="57" width="64" height="4" rx="2" fill="currentColor" fillOpacity="0.3" />
        </svg>
      )}
    </div>
  );
}

/**
 * The estimate, the same line in every card that has a result: «Estimación: 34/100» (with how it moved). What
 * the number is, and is not, is one press away. Where the state already says there is nothing yet (not analysed,
 * queued, running) the line is left out, as it would only repeat it; after a failed analysis it says so.
 */
function CardEstimate({
  score,
  delta,
  state,
}: {
  score: number | null;
  delta: number | null;
  state: CardState;
}) {
  const { t, i18n } = useTranslation();
  if (score === null) {
    if (state === 'pending' || state === 'queued' || state === 'running') return null;
    return <p className="site-card-estimate site-card-muted">{t('home.card.noEstimate')}</p>;
  }
  const points = delta === null ? 0 : Math.abs(delta);
  const improved = delta !== null && delta > 0;
  return (
    <div className="site-card-estimate">
      <p className="site-card-estimate-score">
        <span>
          {t('home.card.estimate')} <strong>{score}/100</strong>
        </span>
        <HelpPopover label={t('home.card.estimateHelp')}>
          <p>{t('home.card.estimateHelpText')}</p>
          <a href={publicPage(i18n.language, 'methodology')}>{t('home.card.estimateMethod')}</a>
        </HelpPopover>
      </p>
      {delta !== null && delta !== 0 && (
        <p className="trend-delta" data-trend={improved ? 'up' : 'down'}>
          {improved ? <ArrowUpIcon aria-hidden="true" /> : <ArrowDownIcon aria-hidden="true" />}
          {improved ? t('home.card.deltaUp', { count: points }) : t('home.card.deltaDown', { count: points })}
        </p>
      )}
    </div>
  );
}

function SiteCard({ site, overview, mayScan }: { site: Site; overview?: SiteOverview; mayScan: boolean }) {
  const { t, i18n } = useTranslation();
  const state = cardState(overview);
  const latest = overview?.latest;
  const done = overview?.lastDone ?? null;
  // Results are shown for an analysis that produced them; after one that could not be completed, the
  // card says so and nothing else about findings (an older result would contradict it).
  const showsResults = (state === 'complete' || state === 'partial') && done !== null;
  const history = (overview?.history ?? [])
    .map((point) => point.score)
    .filter((score): score is number => score !== null);
  const delta = history.length >= 2 ? history.at(-1)! - history.at(-2)! : null;
  // Without the scheme and the closing slash, an address fits on one line of a compact card.
  const address = site.url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  const tone = showsResults ? worstTone(done.summary) : 'none';
  const date = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(iso));
  const dateLine =
    state === 'pending'
      ? t('home.card.never')
      : state === 'queued' || state === 'running'
        ? t('home.card.requested', { date: date(latest!.createdAt) })
        : state === 'failed'
          ? t('home.card.attempt', { date: date(latest!.createdAt) })
          : t('home.card.lastAnalysis', { date: date(latest!.createdAt) });
  const earlier =
    state === 'failed' && done ? t('home.card.lastCompleted', { date: date(done.createdAt) }) : null;
  const notAnalysed = state === 'partial' ? (latest?.pagesNotAnalysed ?? 0) : 0;
  const score = showsResults ? (done.scores?.accesibilidad.score ?? null) : null;
  const action =
    state === 'pending' || state === 'failed'
      ? mayScan
        ? { key: state === 'pending' ? 'prepare' : 'retry', search: { preparar: true as const } }
        : { key: 'open', search: undefined }
      : { key: state === 'queued' || state === 'running' ? 'progress' : 'results', search: undefined };
  return (
    <li className="site-card" data-tone={tone} data-state={state}>
      <CardHeader
        key={overview?.cardPreview ? `preview-${site.id}` : site.id}
        site={site}
        thumbnail={overview?.thumbnail ?? null}
        cardPreview={overview?.cardPreview}
      />
      <div className="site-card-body">
        {/* Name and address, then the state, the estimate, the findings and the ownership, in the same
            order and with the same room in every card; the action is always the last thing. */}
        <div className="site-card-head">
          <span className="site-card-favicon">
            <SiteFavicon siteId={site.id} />
          </span>
          <div className="min-w-0">
            <h3>
              {/* The whole card is this link's target (a stretched ::after), so the card is one click for the
                  mouse and one stop for the keyboard; its buttons and help stay apart, above it. */}
              <Link
                to="/sites/$siteId"
                params={{ siteId: site.id }}
                title={site.name}
                className="site-card-link"
              >
                {site.name}
              </Link>
            </h3>
            <p className="site-card-muted" title={site.url}>
              {address}
            </p>
          </div>
        </div>
        <div className="site-card-status">
          <p className="site-card-state" data-outcome={state}>
            {state === 'pending' ? <ClockIcon aria-hidden="true" /> : <OutcomeIcon outcome={state} />}
            {state === 'pending' ? t('home.card.pending') : t(`status.outcomes.${state}`)}
          </p>
          <p className="site-card-muted">{dateLine}</p>
          {earlier && <p className="site-card-muted">{earlier}</p>}
          {notAnalysed > 0 && (
            <p className="site-card-muted">{t('home.card.notAnalysed', { count: notAnalysed })}</p>
          )}
        </div>
        <CardEstimate score={score} delta={showsResults ? delta : null} state={state} />
        <div className="site-card-findings-slot">
          {showsResults && overview?.pending && (
            <CardFindings pending={overview.pending} partial={state === 'partial'} />
          )}
        </div>
        <CardOwnership status={site.verification.status} />
        <Link
          to="/sites/$siteId"
          params={{ siteId: site.id }}
          search={action.search}
          className={`${buttonClass('primary')} site-card-action`}
        >
          {t(`home.card.actions.${action.key}`)}
          <span className="sr-only"> — {site.name}</span>
        </Link>
      </div>
    </li>
  );
}

export function HomePage() {
  const { t } = useTranslation();
  const { data: me } = useQuery(meQuery);
  const sites = useQuery(sitesQuery);
  const overview = useQuery(overviewQuery);
  const [search, setSearch] = useState('');
  if (!me?.organization) return null;
  const mayAdd = can(me, 'site', 'create');
  const mayScan = can(me, 'site', 'scan');
  const filtered =
    sites.data?.filter((site) =>
      `${site.name} ${site.url}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
    ) ?? [];
  const bySite = new Map((overview.data ?? []).map((entry) => [entry.siteId, entry]));
  return (
    <Page title={t('home.title')} wide>
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <p className="text-ink-muted">{t('home.subtitle')}</p>
        {/* With websites: add from the header. Empty state has its own button, so this one stays hidden. */}
        {mayAdd && sites.isSuccess && sites.data.length > 0 && (
          <Link to="/sites/new" className={buttonClass('primary')}>
            <PlusIcon aria-hidden="true" />
            {t('home.add')}
          </Link>
        )}
      </div>
      {sites.isSuccess && sites.data.length > 0 && (
        <Overview sites={sites.data} overview={overview.data ?? []} />
      )}
      {sites.isPending ? (
        <div role="status">
          <span className="sr-only">{t('app.loading')}</span>
          <div className="skeleton" />
          <div className="skeleton" />
        </div>
      ) : sites.isError ? (
        <Message tone="error">{t('errors.generic')}</Message>
      ) : sites.data.length === 0 ? (
        <section aria-labelledby="sin-webs" className="empty-panel">
          <GlobeIcon width={28} height={28} aria-hidden="true" />
          <h2 id="sin-webs">{t('home.empty')}</h2>
          <p>{mayAdd ? t('home.emptyHint') : t('home.emptyReadOnly')}</p>
          {mayAdd && (
            <Link to="/sites/new" className={buttonClass('primary')}>
              <PlusIcon aria-hidden="true" />
              {t('home.add')}
            </Link>
          )}
        </section>
      ) : (
        <section aria-labelledby="web-list-title">
          <div className="sites-toolbar">
            <h2 id="web-list-title">{t('home.listTitle')}</h2>
            <div className="sites-search">
              <Field
                type="search"
                label={t('home.search')}
                placeholder={t('home.search')}
                hideLabel
                value={search}
                onChange={setSearch}
              />
            </div>
          </div>
          <p className="sr-only" role="status">
            {t('home.results', { count: filtered.length })}
          </p>
          {filtered.length === 0 ? (
            <div className="empty-panel">
              <p>{t('home.noResults')}</p>
            </div>
          ) : (
            <ul className="site-cards">
              {filtered.map((site) => (
                <SiteCard key={site.id} site={site} overview={bySite.get(site.id)} mayScan={mayScan} />
              ))}
            </ul>
          )}
        </section>
      )}
    </Page>
  );
}
